(() => {
  "use strict";

  const root = document.querySelector("[data-morfone-support-page]");
  if (!root) return;
  const SUPPORT_EMAIL = "omj@morfone.com";
  const SUPPORT_REFERENCE = "RLS-SUPPORT-2026";
  const TRACKING_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_content"];
  const tracking = Object.fromEntries(
    TRACKING_KEYS.map((key) => {
      const value = new URLSearchParams(window.location.search).get(key) || "";
      return [key, /^[a-z0-9_-]{1,80}$/i.test(value) ? value : ""];
    }),
  );
  const form = root.querySelector("#support-form");
  const topicCards = [...root.querySelectorAll("[data-topic]")];
  const topicSelect = root.querySelector("#issue-category");
  const productType = root.querySelector("#product-type");
  const description = root.querySelector("#issue-description");
  const subjectPreview = root.querySelector("#subject-preview");
  const selectionStatus = root.querySelector("#selection-status");
  const descriptionCount = root.querySelector("#description-count");
  const toast = root.querySelector("#toast");
  const copyEmailButton = root.querySelector("#copy-email");
  const copyMessageButton = root.querySelector("#copy-message");
  const manualCopyPanel = root.querySelector("#manual-copy-panel");
  const manualCopyText = root.querySelector("#manual-copy-text");
  const closeManualCopyButton = root.querySelector("#close-manual-copy");
  const mobileSupportCta = root.querySelector(".mobile-support-cta");
  const heroPrimaryAction = root.querySelector(".hero-actions .button-primary");
  const directEmailLinks = [...root.querySelectorAll("[data-email-method]")];
  let toastTimer;

  if (!form || !topicSelect || !productType || !description) return;

  const fields = {
    category: topicSelect,
    name: root.querySelector("#customer-name"),
    productType,
    model: root.querySelector("#product-model"),
    serial: root.querySelector("#serial-number"),
    order: root.querySelector("#order-number"),
    channel: root.querySelector("#purchase-channel"),
    date: root.querySelector("#purchase-date"),
    description,
    tried: root.querySelector("#tried-details"),
  };

  const clean = (value, fallback = "Not provided") => {
    const normalized = String(value || "").replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
    return normalized || fallback;
  };

  const publishEvent = (name, details = {}) => {
    const payload = {
      ...tracking,
      support_topic: fields.category?.value || "",
      product_type: fields.productType?.value || "",
      ...details,
    };

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch {
      // Analytics must never block the support flow.
    }
  };

  const trackingLines = () => [
    `Support card reference: ${SUPPORT_REFERENCE}`,
    `Support source: ${tracking.utm_id || "rls_2026_01"}`,
    `Campaign source: ${tracking.utm_source || "direct"}`,
    `Campaign medium: ${tracking.utm_medium || "direct"}`,
    `Campaign content: ${tracking.utm_content || "not_provided"}`,
  ];

  const multiline = (value, fallback = "Not provided") => {
    const normalized = String(value || "").replace(/\r\n/g, "\n").trim();
    return normalized || fallback;
  };

  const setToast = (message) => {
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add("is-visible");
    toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 2800);
  };

  const updateSubject = () => {
    const category = clean(fields.category.value, "General Question");
    const product = clean(fields.model.value, clean(fields.productType.value, "Red Light Product"));
    subjectPreview.textContent = `Morfone Support | ${category} | ${product}`;
  };

  const selectTopic = (topic, { scroll = false } = {}) => {
    topicCards.forEach((card) => card.setAttribute("aria-pressed", String(card.dataset.topic === topic)));
    topicSelect.value = topic;
    topicSelect.removeAttribute("aria-invalid");
    root.querySelector('[data-error-for="issue-category"]').textContent = "";
    selectionStatus.textContent = `${topic} selected. Add your details below.`;
    updateSubject();
    publishEvent("morfone_support:topic_selected", { support_topic: topic });

    if (scroll) {
      root.querySelector("#support")?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => topicSelect.focus({ preventScroll: true }), 500);
    }
  };

  topicCards.forEach((card) => {
    card.addEventListener("click", () => selectTopic(card.dataset.topic, { scroll: true }));
  });

  topicSelect.addEventListener("change", () => {
    const value = topicSelect.value;
    topicCards.forEach((card) => card.setAttribute("aria-pressed", String(card.dataset.topic === value)));
    selectionStatus.textContent = value ? `${value} selected.` : "No topic selected yet.";
    updateSubject();
    if (value) publishEvent("morfone_support:topic_selected", { support_topic: value });
  });

  fields.model.addEventListener("input", updateSubject);
  fields.productType.addEventListener("change", updateSubject);

  description.addEventListener("input", () => {
    descriptionCount.value = description.value.length;
  });

  const buildMessage = () => {
    const category = clean(fields.category.value, "General Question");
    const product = clean(fields.model.value, clean(fields.productType.value, "Red Light Product"));
    const subject = `Morfone Support | ${category} | ${product}`;
    const body = [
      "Hello Morfone Support,",
      "",
      "I need help with the following:",
      "",
      `Issue category: ${category}`,
      `Name: ${clean(fields.name.value)}`,
      `Product or model: ${clean(fields.model.value)}`,
      `Product type: ${clean(fields.productType.value)}`,
      `Serial number: ${clean(fields.serial.value)}`,
      `Order number: ${clean(fields.order.value)}`,
      `Purchased from: ${clean(fields.channel.value)}`,
      `Purchase date: ${clean(fields.date.value)}`,
      "",
      "What happened:",
      multiline(fields.description.value),
      "",
      "What I have already tried:",
      multiline(fields.tried.value),
      "",
      "I can add photos or a short video if needed.",
      "",
      ...trackingLines(),
      "",
      "Thank you.",
    ].join("\r\n");

    return { subject, body };
  };

  const validate = () => {
    const checks = [
      [fields.category, "Choose the closest support topic."],
      [fields.productType, "Choose a product type or select “Other or not sure.”"],
      [fields.description, "Please briefly describe what happened."],
    ];
    let firstInvalid = null;

    checks.forEach(([field, message]) => {
      const error = root.querySelector(`[data-error-for="${field.id}"]`);
      const invalid = !String(field.value || "").trim();
      field.setAttribute("aria-invalid", String(invalid));
      if (error) error.textContent = invalid ? message : "";
      if (invalid && !firstInvalid) firstInvalid = field;
    });

    if (firstInvalid) {
      firstInvalid.focus();
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      setToast("Add the three required details before opening your email.");
      return false;
    }
    return true;
  };

  const fallbackCopy = (text) => {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    root.append(area);
    area.select();
    const copied = document.execCommand("copy");
    area.remove();
    if (!copied) throw new Error("Copy command was not available.");
  };

  const showManualCopy = (text) => {
    if (!manualCopyPanel || !manualCopyText) return;
    manualCopyText.value = text;
    manualCopyPanel.hidden = false;
    manualCopyPanel.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      manualCopyText.focus({ preventScroll: true });
      manualCopyText.select();
    }, 350);
  };

  const copyText = async (text, successMessage) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          setToast(successMessage);
          return;
        } catch {
          // Some in-app browsers expose Clipboard API but deny the write.
          // Fall through to the selection-based copy path.
        }
      }
      fallbackCopy(text);
      setToast(successMessage);
    } catch {
      showManualCopy(text);
      setToast("Automatic copy was blocked. The text is selected for manual copy.");
    }
  };

  closeManualCopyButton?.addEventListener("click", () => {
    manualCopyPanel.hidden = true;
  });

  copyEmailButton.addEventListener("click", () => {
    publishEvent("morfone_support:email_intent", { method: "copy_address" });
    copyText(SUPPORT_EMAIL, "Support email copied.");
  });
  copyMessageButton.addEventListener("click", () => {
    const { subject, body } = buildMessage();
    publishEvent("morfone_support:email_intent", { method: "copy_message" });
    copyText(`To: ${SUPPORT_EMAIL}\nSubject: ${subject}\n\n${body}`, "Message details copied.");
  });

  directEmailLinks.forEach((link) => {
    link.addEventListener("click", () => {
      const subject = "Morfone Red Light Product Support";
      const body = [
        "Hello Morfone Support,",
        "",
        "I need help with a red light product.",
        "",
        ...trackingLines(),
        "",
        "Thank you.",
      ].join("\r\n");
      link.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      publishEvent("morfone_support:email_intent", { method: link.dataset.emailMethod || "direct" });
    });
  });

  form.addEventListener("input", (event) => {
    const field = event.target;
    if (!field.id) return;
    const error = root.querySelector(`[data-error-for="${field.id}"]`);
    if (error && String(field.value || "").trim()) {
      error.textContent = "";
      field.removeAttribute("aria-invalid");
    }
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!validate()) return;

    const { subject, body } = buildMessage();
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    publishEvent("morfone_support:email_intent", { method: "builder" });
    setToast("Opening your email app…");
    window.location.assign(href);
  });

  if (mobileSupportCta) {
    if (heroPrimaryAction && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => mobileSupportCta.classList.toggle("is-active", !entry.isIntersecting),
        { threshold: 0.35 },
      );
      observer.observe(heroPrimaryAction);
    } else {
      mobileSupportCta.classList.add("is-active");
    }
  }

  const formControlSelector = "input, select, textarea, [contenteditable='true']";
  document.addEventListener("focusin", (event) => {
    if (event.target.matches?.(formControlSelector)) {
      document.body.classList.add("is-support-form-focused");
    }
  });
  document.addEventListener("focusout", () => {
    window.setTimeout(() => {
      if (!document.activeElement?.matches?.(formControlSelector)) {
        document.body.classList.remove("is-support-form-focused");
      }
    }, 0);
  });

  if (window.visualViewport) {
    let maximumViewportHeight = window.visualViewport.height;
    const updateKeyboardState = () => {
      maximumViewportHeight = Math.max(maximumViewportHeight, window.visualViewport.height);
      document.body.classList.toggle(
        "is-support-keyboard-open",
        maximumViewportHeight - window.visualViewport.height > 140,
      );
    };
    window.visualViewport.addEventListener("resize", updateKeyboardState);
  }

  updateSubject();
})();
